import { Badge, MultiSelect, useSafeRouterSearchParams } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import React from 'react'
import type { FC } from 'react'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import { useGetLockedDays } from '../hooks'
import { getIsoDaysInWeek } from '../utils/getIsoDaysInWeek'

type Props = {
  selectedDays: { value: string; label: string }[]
  control: Control
  errors: FieldErrors
  setValue: Function
  date?: string | Date
}
export const WeekdaysFields: FC<Props> = ({
  selectedDays,
  control,
  errors,
  setValue,
  date
}) => {
  const { t, tDates } = usePlanningFormTranslation()
  const [searchParams] = useSafeRouterSearchParams()
  const isoDays = getIsoDaysInWeek(date ?? searchParams.date)
  const { lockedDays } = useGetLockedDays(isoDays)

  const days = isoDays.map(day => ({
    value: day,
    label: tDates('dates.literalDate', { date: day }),
    disabled: lockedDays?.[day]?.isLocked ?? false
  }))
  const oneIsDisabled = days.find(i => i.disabled)

  // We want the object (label, value, highlight), not just the number value
  const handleChange = (newValues: string[]) => {
    const updatedValues = newValues.map(item => {
      return days.find(option => option.value === item)
    })
    setValue('days', updatedValues)
  }

  return (
    <Controller
      name='days'
      control={control}
      render={({ field: { value } }) => (
        <MultiSelect
          selectAll={!oneIsDisabled}
          clearable
          searchable
          fillWidth
          className='mb-2'
          dropdownHeight='m'
          dropdownWidth='target'
          options={days}
          onChange={handleChange}
          placeholder={t('component.fieldWeekday.placeholder')}
          value={value.map(x => x.value)}
          errorMessage={formatErrors({
            inputName: 'days',
            errors
          })}
          label={
            <>
              {t('component.fieldWeekday.title')}
              {selectedDays?.length > 0 && (
                <Badge
                  size='s'
                  label={selectedDays.length.toString()}
                  className='ml-2'
                />
              )}
            </>
          }
        />
      )}
    />
  )
}
